// src/components/sidebar/notes/notes.hooks.ts (USER APP)
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { userNotesApi } from "../../../../api/notes/notes";
import type { NoteResponse } from "../../../../api/notes/notes.types";

function formatDateTime(isoOrDate: string | Date): string {
    const d = typeof isoOrDate === "string" ? new Date(isoOrDate) : isoOrDate;
    if (Number.isNaN(d.getTime())) {
        return typeof isoOrDate === "string" ? isoOrDate : "";
    }
    return d.toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
    });
}

function deriveSafeTitle(title: string, body: string): string {
    const t = (title ?? "").trim();
    if (t) return t.slice(0, 200);

    const firstLine = (body ?? "")
        .split("\n")
        .map((x) => x.trim())
        .find(Boolean) ?? "";

    return (firstLine || "Untitled").slice(0, 200);
}

// Backend requires content min_length 1. Allow "title-only" notes by storing a single space.
function deriveSafeContent(body: string): string {
    const b = body ?? "";
    return b.length > 0 ? b.slice(0, 20000) : " ";
}

type Options = {
    noteId: string | null;
    open: boolean;

    // exactOptionalPropertyTypes: caller may pass undefined explicitly
    onSaved?: (() => void) | undefined;
    onDeleted?: (() => void) | undefined;
};

type Draft = { title: string; body: string };

export const useNotes = ({
    noteId,
    open,
    onSaved,
    onDeleted,
}: Options) => {
    const [title, _setTitle] = useState("");
    const [body, _setBody] = useState("");

    const [activeNoteId, _setActiveNoteId] = useState<string | null>(noteId);

    // Mirror of activeNoteId in a ref so flushSave always reads the current
    // value even when invoked from a stale debounce-timer closure.
    const activeNoteIdRef = useRef<string | null>(noteId);
    const setActiveNoteId = useCallback((id: string | null) => {
        activeNoteIdRef.current = id;
        _setActiveNoteId(id);
    }, []);

    const [createdAtIso, setCreatedAtIso] = useState<string>(() =>
        new Date().toISOString()
    );
    const [updatedAtIso, setUpdatedAtIso] = useState<string>(() =>
        new Date().toISOString()
    );

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);

    // Session guard (prevents late async writes to a new session)
    const sessionRef = useRef(0);

    // Debounce autosave
    const debounceTimerRef = useRef<number | null>(null);

    // Latest draft snapshot (prevents stale closures)
    const draftRef = useRef<Draft>({ title: "", body: "" });

    // Dirty + save loop control
    const dirtyRef = useRef(false);
    const saveLoopRunningRef = useRef(false);

    // Ensure we NEVER create more than one note per session
    const createPromiseRef = useRef<Promise<NoteResponse> | null>(null);

    // Avoid autosave while loading
    const suppressAutosaveRef = useRef(false);

    // Tracks which noteId was last successfully loaded (enables stale-while-revalidate on reopen)
    const lastLoadedNoteIdRef = useRef<string | null>(null);

    const clearDebounce = useCallback(() => {
        if (debounceTimerRef.current) {
            window.clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
    }, []);

    const resetNote = useCallback(() => {
        clearDebounce();

        // new session for a fresh draft
        sessionRef.current += 1;

        setActiveNoteId(null);
        setLoadError(null);
        setSaveError(null);
        _setTitle("");
        _setBody("");
        draftRef.current = { title: "", body: "" };

        const now = new Date().toISOString();
        setCreatedAtIso(now);
        setUpdatedAtIso(now);

        dirtyRef.current = false;
        saveLoopRunningRef.current = false;

        createPromiseRef.current = null;
        lastLoadedNoteIdRef.current = null;
    }, [clearDebounce]);

    const applyRemoteNote = useCallback(
        (n: NoteResponse, mySession: number) => {
            // Session guard
            if (sessionRef.current !== mySession) return;

            const t = String(n.title ?? "");
            const c = String(n.content ?? "").replace(/\s+$/g, ""); // trimEnd only

            const nId = String(n.id);
            setActiveNoteId(nId);
            lastLoadedNoteIdRef.current = nId;
            _setTitle(t);
            _setBody(c);
            draftRef.current = { title: t, body: c };

            setCreatedAtIso(
                String(n.created_at ?? new Date().toISOString()),
            );
            setUpdatedAtIso(
                String(
                    n.updated_at ??
                        n.created_at ??
                        new Date().toISOString(),
                ),
            );

            // If we applied remote state, it becomes the new baseline
            dirtyRef.current = false;
            saveLoopRunningRef.current = false;

            // If a create promise from some older draft existed, drop it
            createPromiseRef.current = null;
        },
        [],
    );

    // Internal: start a single save loop (create once, then update)
    const flushSave = useCallback(async () => {
        if (!open) return;
        if (suppressAutosaveRef.current) return;
        if (isLoading) return;

        // mark dirty; coalesce into a single loop
        dirtyRef.current = true;
        if (saveLoopRunningRef.current) return;

        saveLoopRunningRef.current = true;

        const mySession = sessionRef.current;

        // Read from the ref so even a stale closure gets the current value.
        let id = activeNoteIdRef.current;

        try {
            while (dirtyRef.current) {
                dirtyRef.current = false;

                const snap = draftRef.current;
                const hasAny = (snap.title ?? "").trim().length > 0 ||
                    (snap.body ?? "").trim().length > 0;

                // Don't create/update an empty note
                if (!hasAny) continue;

                const safeTitle = deriveSafeTitle(snap.title, snap.body);
                const safeContent = deriveSafeContent(snap.body);

                setIsSaving(true);
                setSaveError(null);

                if (!id) {
                    if (!createPromiseRef.current) {
                        createPromiseRef.current = userNotesApi.createNote({
                            title: safeTitle,
                            content: safeContent,
                        });
                    }

                    const created = await createPromiseRef.current;

                    // Session guard: if user closed/reopened, ignore
                    if (sessionRef.current !== mySession) return;

                    id = String(created.id);
                    setActiveNoteId(id);

                    setCreatedAtIso(
                        String(
                            created.created_at ??
                                new Date().toISOString(),
                        ),
                    );
                    setUpdatedAtIso(
                        String(
                            created.updated_at ??
                                created.created_at ??
                                new Date().toISOString(),
                        ),
                    );

                    // Keep createPromiseRef alive (don't null it here).
                    // A stale flushSave with id=null will re-await the same
                    // resolved promise and get the existing ID instead of
                    // issuing a second CREATE. Cleared only in resetNote.
                } else {
                    // 2) Update existing note
                    const updated = await userNotesApi.updateNote(id, {
                        title: safeTitle,
                        content: safeContent,
                    });

                    if (sessionRef.current !== mySession) return;

                    setUpdatedAtIso(
                        String(
                            updated.updated_at ??
                                new Date().toISOString(),
                        ),
                    );
                }

                onSaved?.();
            }
        } catch (err: any) {
            console.error("Failed to save note", err);
            const status = err?.response?.status;
            const serverMsg = err?.response?.data?.detail;
            if (status === 403) {
                setSaveError("You don't have permission to edit this note.");
            } else if (typeof serverMsg === "string" && serverMsg) {
                setSaveError(serverMsg);
            } else {
                setSaveError("Failed to save note.");
            }
            // Keep dirtyRef false; next keystroke will set it again and retry
        } finally {
            if (sessionRef.current === mySession) {
                setIsSaving(false);
            }
            saveLoopRunningRef.current = false;
        }
    }, [isLoading, onSaved, open]);

    const scheduleAutosave = useCallback(() => {
        if (!open) return;
        if (suppressAutosaveRef.current) return;

        clearDebounce();
        debounceTimerRef.current = window.setTimeout(() => {
            void flushSave();
        }, 550);
    }, [clearDebounce, flushSave, open]);

    // Public setters (update state + ref + schedule autosave)
    const setTitle = useCallback(
        (v: string) => {
            _setTitle(v);
            draftRef.current = { ...draftRef.current, title: v };
            scheduleAutosave();
        },
        [scheduleAutosave],
    );

    const setBody = useCallback(
        (v: string) => {
            _setBody(v);
            draftRef.current = { ...draftRef.current, body: v };
            scheduleAutosave();
        },
        [scheduleAutosave],
    );

    // Load note on open / noteId change. Skip fetch if already loaded.
    useEffect(() => {
        if (!open) {
            clearDebounce();
            return;
        }

        // new open session boundary
        sessionRef.current += 1;
        const mySession = sessionRef.current;

        const run = async () => {
            if (!noteId) {
                suppressAutosaveRef.current = true;
                try {
                    resetNote();
                } finally {
                    suppressAutosaveRef.current = false;
                }
                return;
            }

            // Already fetched this note — cached state is still in React, nothing to do.
            if (lastLoadedNoteIdRef.current === noteId) return;

            suppressAutosaveRef.current = true;
            setIsLoading(true);
            setLoadError(null);

            try {
                const n = await userNotesApi.getNote(noteId);
                if (sessionRef.current !== mySession) return;

                applyRemoteNote(n, mySession);
            } catch (err: any) {
                if (sessionRef.current !== mySession) return;

                console.error("Failed to load note", err);
                const status = err?.response?.status;
                if (status === 404) {
                    setLoadError("This note no longer exists.");
                } else if (status === 403) {
                    setLoadError("You don't have access to this note.");
                } else {
                    setLoadError("Failed to load note. Please try again.");
                }
                setActiveNoteId(noteId);
            } finally {
                if (sessionRef.current === mySession) {
                    setIsLoading(false);
                    suppressAutosaveRef.current = false;
                }
            }
        };

        void run();
    }, [applyRemoteNote, clearDebounce, noteId, open, resetNote]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearDebounce();
        };
    }, [clearDebounce]);

    const formattedCreatedAt = useMemo(
        () => formatDateTime(createdAtIso),
        [createdAtIso],
    );
    const formattedUpdatedAt = useMemo(
        () => formatDateTime(updatedAtIso),
        [updatedAtIso],
    );

    const buildEditorText = useCallback(() => {
        return title + (body !== "" ? "\n" + body : "");
    }, [title, body]);

    const copyToClipboard = useCallback(async (): Promise<boolean> => {
        const text = buildEditorText().trimEnd();
        if (!text) return false;
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {
            return false;
        }
    }, [buildEditorText]);

    const saveNow = useCallback(async () => {
        clearDebounce();
        await flushSave();
    }, [clearDebounce, flushSave]);

    const deleteNote = useCallback(async () => {
        clearDebounce();

        // If draft was never persisted, treat as local delete
        if (!activeNoteId) {
            resetNote();
            onDeleted?.();
            return;
        }

        const mySession = sessionRef.current;

        try {
            setIsSaving(true);

            // If a create is in-flight (should not happen when activeNoteId exists, but safe)
            if (createPromiseRef.current) {
                try {
                    const created = await createPromiseRef.current;
                    if (sessionRef.current !== mySession) return;
                    const createdId = String(created.id);
                    await userNotesApi.deleteNote(createdId);
                } catch {
                    // ignore
                } finally {
                    createPromiseRef.current = null;
                }
            }

            await userNotesApi.deleteNote(activeNoteId);

            if (sessionRef.current !== mySession) return;

            resetNote();
            onDeleted?.();
        } catch (err) {
            console.error("Failed to delete note", err);
            throw err;
        } finally {
            if (sessionRef.current === mySession) {
                setIsSaving(false);
            }
        }
    }, [activeNoteId, clearDebounce, onDeleted, resetNote]);

    return {
        // state
        title,
        body,
        activeNoteId,

        // meta
        createdAtIso,
        updatedAtIso,
        formattedCreatedAt,
        formattedUpdatedAt,
        isLoading,
        isSaving,
        loadError,
        saveError,
        hasPersisted: !!activeNoteId,

        setTitle,
        setBody,
        copyToClipboard,
        resetNote,
        saveNow,
        deleteNote,
    };
};
